import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { setAxiosBaseURL } from "../lib/axios";

/*
 * Claves de tipografía disponibles para tenants.json. TODAS las familias ya
 * están cargadas por la app (index.html: Inter/Nunito/Poppins vía Google
 * Fonts; _fonts.scss: hkgrotesk self-hosted; _variables.scss: Unbounded),
 * así que una marca cambia de personalidad sin añadir una sola descarga.
 */
export type TenantFontKey =
  | "inter"
  | "nunito"
  | "poppins"
  | "hkgrotesk"
  | "unbounded"
  | "sora"
  | "system"

const FONT_STACKS: Record<TenantFontKey, string> = {
  inter: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  nunito: '"Nunito", "Inter", system-ui, sans-serif',
  poppins: '"Poppins", "Inter", system-ui, sans-serif',
  hkgrotesk: '"hkgrotesk", "Inter", system-ui, sans-serif',
  sora: "'Sora', system-ui, sans-serif",
  unbounded: '"Unbounded", "Inter", system-ui, sans-serif',
  system: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
}

interface TenantConfig {
  company: string;
  apiUrl: string;
  primaryColor: string;
  secondaryColor: string;
  logoMenu: string;
  logoMenuSm: string;
  logoLogin: string;
  logoBgLogin: string;
  favicon: string;
  hasMayoristas?: boolean;
  /** Manifiesto de marca (opcionales; default inter / 0.75rem): */
  fontFamily?: TenantFontKey;
  fontDisplay?: TenantFontKey;
  borderRadius?: string;
  accentColor?: string;
}

interface TenantContextType {
  tenant: TenantConfig | null;
  loading: boolean;
}

const defaultTenant: TenantConfig = {
  company: "CRM",
  apiUrl: import.meta.env.VITE_BACKEND_URL || "https://api.celink.mx/api",
  primaryColor: import.meta.env.VITE_PRIMARY_COLOR || "#2E5090",
  secondaryColor: import.meta.env.VITE_SECONDARY_COLOR || "#40C4C7",
  logoMenu: import.meta.env.VITE_LOGO_MENU || "/images/logo-menu/default-logo.png",
  logoMenuSm: import.meta.env.VITE_LOGO_MENU_SM || "/images/logo-menu/sm/default-logo-sm.png",
  logoLogin: import.meta.env.VITE_LOGO_LOGIN || "/images/logo-login/default-logo.png",
  logoBgLogin: import.meta.env.VITE_LOGO_BG_LOGIN || "/images/bg-image-login/default-bg.png",
  favicon: "/favicon.ico",
};

const TenantContext = createContext<TenantContextType>({
  tenant: defaultTenant,
  loading: true,
});

export const useTenant = () => useContext(TenantContext);

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTenantConfig = async () => {
      try {
        const response = await fetch("/tenants.json");
        const tenants = await response.json();
        const hostname = window.location.hostname.toLowerCase();
        const pathSlug = window.location.pathname.replace(/^\/+/, "").split("/")[0]?.toLowerCase() || "";

        // Try exact hostname match first
        let tenantConfig = tenants[hostname];

        // If not found, try slug detection (path or hostname substring)
        if (!tenantConfig) {
          if (hostname.includes("claricel") || pathSlug === "claricel") {
            tenantConfig = tenants["crm.claricel.net"];
          } else if (hostname.includes("nxum") || pathSlug === "nxum") {
            tenantConfig = tenants["crm.nxum.mx"];
          } else if (hostname.includes("infynit") || pathSlug === "infynit") {
            tenantConfig = tenants["crm.infynit.mx"];
          } else if (hostname.includes("celink") || pathSlug === "celink") {
            tenantConfig = tenants["crm.celink.mx"];
          } else if (hostname.includes("pandamovil") || pathSlug === "pandamovil" || pathSlug === "panda") {
            tenantConfig = tenants["crm.pandamovil.mx"];
          } else if (hostname.includes("cristocel") || pathSlug === "cristocel") {
            tenantConfig = tenants["crm.cristocel.com"];
          } else if (hostname.includes("figou") || pathSlug === "figou") {
            tenantConfig = tenants["crm.figou.mx"];
          } else if (hostname.includes("igou") || pathSlug === "igou") {
            tenantConfig = tenants["crm.igou.mx"];
          } else if (hostname.includes("beonline") || pathSlug === "beonline") {
            tenantConfig = tenants["crm.beonline.mx"];
          }
        }

        tenantConfig = tenantConfig || tenants["default"] || defaultTenant;
        
        // Update axios baseURL with tenant-specific API URL
        if (tenantConfig.apiUrl) {
          setAxiosBaseURL(tenantConfig.apiUrl);
        }

        // Apply CSS variables for multi-tenant theming
        const primary = tenantConfig.primaryColor;
        const secondary = tenantConfig.secondaryColor;
        
        // Base theme colors with !important to override compiled CSS
        document.documentElement.style.setProperty("--primary-color", primary, "important");
        document.documentElement.style.setProperty("--secondary-color", secondary, "important");
        
        // Velzon theme variables
        document.documentElement.style.setProperty("--vz-primary", primary, "important");
        document.documentElement.style.setProperty("--vz-secondary", secondary, "important");
        document.documentElement.style.setProperty("--vz-blue", primary, "important");
        document.documentElement.style.setProperty("--vz-cyan", secondary, "important");
        document.documentElement.style.setProperty("--vz-purple", primary, "important");
        document.documentElement.style.setProperty("--vz-info", secondary, "important");
        document.documentElement.style.setProperty("--vz-success", primary, "important");
        
        // PrimeReact theme variables
        document.documentElement.style.setProperty("--blue-500", primary, "important");
        document.documentElement.style.setProperty("--cyan-500", secondary, "important");
        document.documentElement.style.setProperty("--purple-500", primary, "important");
        document.documentElement.style.setProperty("--indigo-500", primary, "important");

        /*
         * Manifiesto de marca: tipografía, radio y acento por tenant.
         * shell-modern.css consume --vz-font-sans-serif/--vz-heading-font-family
         * (body y headings); tailwind.css mapea --radius a la escala rounded-*
         * que usa @celink-ui. Todo opcional: sin las claves, el tenant queda
         * en Inter / 0.75rem (los defaults de las hojas de estilo).
         */
        const bodyFont =
          FONT_STACKS[tenantConfig.fontFamily ?? "inter"] ?? FONT_STACKS.inter;
        const displayFont =
          FONT_STACKS[tenantConfig.fontDisplay ?? tenantConfig.fontFamily ?? "inter"] ??
          bodyFont;
        // Se fija en documentElement Y en body: el tema de Velzon REDEFINE
        // --vz-font-sans-serif sobre `body`, asi que ponerla solo en :root no
        // llegaba al contenido (medido en app_web: Sora en html, Unbounded en body).
        for (const el of [document.documentElement, document.body]) {
          if (!el) continue;
          el.style.setProperty("--vz-font-sans-serif", bodyFont, "important");
          el.style.setProperty("--vz-heading-font-family", displayFont, "important");
        }
        if (tenantConfig.borderRadius) {
          document.documentElement.style.setProperty("--radius", tenantConfig.borderRadius, "important");
          document.documentElement.style.setProperty("--vz-border-radius", tenantConfig.borderRadius, "important");
        }
        if (tenantConfig.accentColor) {
          document.documentElement.style.setProperty("--vz-brand-accent", tenantConfig.accentColor, "important");
        }

        // Convert hex to RGB for rgba() usage
        const hexToRgb = (hex: string) => {
          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
          return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
          } : null;
        };

        const clamp255 = (n: number) => Math.max(0, Math.min(255, Math.round(n)));

        const rgbToHex = (r: number, g: number, b: number) => {
          return (
            "#" +
            [clamp255(r), clamp255(g), clamp255(b)]
              .map((v) => v.toString(16).padStart(2, "0"))
              .join("")
          );
        };

        const mixHex = (hexA: string, hexB: string, weightB: number) => {
          const a = hexToRgb(hexA);
          const b = hexToRgb(hexB);
          if (!a || !b) return hexA;
          const w = Math.max(0, Math.min(1, weightB));
          return rgbToHex(
            a.r * (1 - w) + b.r * w,
            a.g * (1 - w) + b.g * w,
            a.b * (1 - w) + b.b * w
          );
        };

        const darkenHex = (hex: string, amount: number) => mixHex(hex, "#000000", amount);
        const lightenHex = (hex: string, amount: number) => mixHex(hex, "#ffffff", amount);

        // Triplete HSL "H S% L%" para las variables shadcn (--primary, --ring),
        // que tailwind.css consume como hsl(var(--primary)).
        const hexToHslTriplet = (hex: string): string | null => {
          const rgb = hexToRgb(hex);
          if (!rgb) return null;
          const r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
          const max = Math.max(r, g, b), min = Math.min(r, g, b);
          const l = (max + min) / 2;
          let h = 0, s = 0;
          if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
            else if (max === g) h = ((b - r) / d + 2) / 6;
            else h = ((r - g) / d + 4) / 6;
          }
          return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
        };


        // Derived tokens to avoid compile-time hardcodes
        const primaryTextEmphasis = darkenHex(primary, 0.18);
        const primaryBgSubtle = lightenHex(primary, 0.88);
        const primaryBorderSubtle = lightenHex(primary, 0.70);

        const secondaryTextEmphasis = darkenHex(secondary, 0.18);
        const secondaryBgSubtle = lightenHex(secondary, 0.88);
        const secondaryBorderSubtle = lightenHex(secondary, 0.70);

        document.documentElement.style.setProperty("--vz-primary-text-emphasis", primaryTextEmphasis, "important");
        document.documentElement.style.setProperty("--vz-primary-bg-subtle", primaryBgSubtle, "important");
        document.documentElement.style.setProperty("--vz-primary-border-subtle", primaryBorderSubtle, "important");
        document.documentElement.style.setProperty("--vz-secondary-text-emphasis", secondaryTextEmphasis, "important");
        document.documentElement.style.setProperty("--vz-secondary-bg-subtle", secondaryBgSubtle, "important");
        document.documentElement.style.setProperty("--vz-secondary-border-subtle", secondaryBorderSubtle, "important");

        // Keep these aligned with our mapping (success -> primary, info -> secondary)
        document.documentElement.style.setProperty("--vz-success-text-emphasis", primaryTextEmphasis, "important");
        document.documentElement.style.setProperty("--vz-success-bg-subtle", primaryBgSubtle, "important");
        document.documentElement.style.setProperty("--vz-success-border-subtle", primaryBorderSubtle, "important");
        document.documentElement.style.setProperty("--vz-info-text-emphasis", secondaryTextEmphasis, "important");
        document.documentElement.style.setProperty("--vz-info-bg-subtle", secondaryBgSubtle, "important");
        document.documentElement.style.setProperty("--vz-info-border-subtle", secondaryBorderSubtle, "important");
        
        const primaryRgb = hexToRgb(primary);
        const secondaryRgb = hexToRgb(secondary);
        
        if (primaryRgb) {
          const pRgb = `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`;
          document.documentElement.style.setProperty("--vz-primary-rgb", pRgb, "important");
          document.documentElement.style.setProperty("--vz-success-rgb", pRgb, "important");
          
          // Menu/header variables
          document.documentElement.style.setProperty("--vz-vertical-menu-item-hover-color", primary, "important");
          document.documentElement.style.setProperty("--vz-vertical-menu-sub-item-hover-color", primary, "important");
          document.documentElement.style.setProperty("--vz-vertical-menu-item-active-color", primary, "important");
          document.documentElement.style.setProperty("--vz-vertical-menu-sub-item-active-color", primary, "important");
          document.documentElement.style.setProperty("--vz-vertical-menu-item-bg", `rgba(${pRgb}, .08)`, "important");
          document.documentElement.style.setProperty("--vz-vertical-menu-item-active-bg", `rgba(${pRgb}, .15)`, "important");
          document.documentElement.style.setProperty("--vz-topnav-item-active-color", primary, "important");
        }
        if (secondaryRgb) {
          const sRgb = `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`;
          document.documentElement.style.setProperty("--vz-secondary-rgb", sRgb, "important");
          document.documentElement.style.setProperty("--vz-info-rgb", sRgb, "important");
          document.documentElement.style.setProperty("--vz-header-item-bg", `rgba(${sRgb}, .12)`, "important");
          document.documentElement.style.setProperty("--focus-ring", `0 0 0 .2rem rgba(${sRgb}, .35)`, "important");
          document.documentElement.style.setProperty("--highlight-text-color", secondary, "important");
        }

        // Inject a <style> tag to override :root CSS rules
        const styleId = 'tenant-override-styles';
        let styleTag = document.getElementById(styleId) as HTMLStyleElement;
        if (!styleTag) {
          styleTag = document.createElement('style');
          styleTag.id = styleId;
          document.head.appendChild(styleTag);
        }
        const pRgbStr = primaryRgb ? `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}` : '46, 80, 144';
        const sRgbStr = secondaryRgb ? `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}` : '64, 196, 199';
        // Dark mode derived tokens
        const darkPrimaryTextEmphasis = lightenHex(primary, 0.20);
        const darkPrimaryBgSubtle = `rgba(${pRgbStr}, 0.15)`;
        const darkPrimaryBorderSubtle = darkenHex(primary, 0.35);
        const darkSecondaryTextEmphasis = lightenHex(secondary, 0.20);
        const darkSecondaryBgSubtle = `rgba(${sRgbStr}, 0.15)`;
        const darkSecondaryBorderSubtle = darkenHex(secondary, 0.50);

        // Tokens shadcn (@celink/ui + clases text-primary/bg-primary de pages):
        // marca en light tal cual; en dark aclarada para contraste sobre #1a1d21
        const shadcnPrimaryLight = hexToHslTriplet(primary);
        const shadcnPrimaryDark = hexToHslTriplet(lightenHex(primary, 0.25));

        styleTag.textContent = `
          :root, [data-bs-theme=light] {
            --vz-primary: ${primary} !important;
            --vz-secondary: ${secondary} !important;
            --vz-blue: ${primary} !important;
            --vz-cyan: ${secondary} !important;
            --vz-purple: ${primary} !important;
            --vz-info: ${secondary} !important;
            --vz-success: ${primary} !important;
            --vz-primary-rgb: ${pRgbStr} !important;
            --vz-secondary-rgb: ${sRgbStr} !important;
            --vz-success-rgb: ${pRgbStr} !important;
            --vz-info-rgb: ${sRgbStr} !important;
            --vz-primary-text-emphasis: ${primaryTextEmphasis} !important;
            --vz-primary-bg-subtle: ${primaryBgSubtle} !important;
            --vz-primary-border-subtle: ${primaryBorderSubtle} !important;
            --vz-secondary-text-emphasis: ${secondaryTextEmphasis} !important;
            --vz-secondary-bg-subtle: ${secondaryBgSubtle} !important;
            --vz-secondary-border-subtle: ${secondaryBorderSubtle} !important;
            ${shadcnPrimaryLight ? `--primary: ${shadcnPrimaryLight};\n            --ring: ${shadcnPrimaryLight};` : ""}
          }
          [data-bs-theme=dark] {
            --vz-primary: ${primary} !important;
            --vz-secondary: ${secondary} !important;
            --vz-blue: ${primary} !important;
            --vz-cyan: ${secondary} !important;
            --vz-purple: ${primary} !important;
            --vz-info: ${secondary} !important;
            --vz-success: ${primary} !important;
            --vz-primary-rgb: ${pRgbStr} !important;
            --vz-secondary-rgb: ${sRgbStr} !important;
            --vz-success-rgb: ${pRgbStr} !important;
            --vz-info-rgb: ${sRgbStr} !important;
            --vz-primary-text-emphasis: ${darkPrimaryTextEmphasis} !important;
            --vz-primary-bg-subtle: ${darkPrimaryBgSubtle} !important;
            --vz-primary-border-subtle: ${darkPrimaryBorderSubtle} !important;
            --vz-secondary-text-emphasis: ${darkSecondaryTextEmphasis} !important;
            --vz-secondary-bg-subtle: ${darkSecondaryBgSubtle} !important;
            --vz-secondary-border-subtle: ${darkSecondaryBorderSubtle} !important;
            --vz-success-text-emphasis: ${darkPrimaryTextEmphasis} !important;
            --vz-success-bg-subtle: ${darkPrimaryBgSubtle} !important;
            --vz-success-border-subtle: ${darkPrimaryBorderSubtle} !important;
            --vz-info-text-emphasis: ${darkSecondaryTextEmphasis} !important;
            --vz-info-bg-subtle: ${darkSecondaryBgSubtle} !important;
            --vz-info-border-subtle: ${darkSecondaryBorderSubtle} !important;
            --vz-body-bg: #1a1d21 !important;
            --vz-body-bg-rgb: 26, 29, 33 !important;
            --vz-body-color: #ced4da !important;
            --vz-body-color-rgb: 206, 212, 218 !important;
            --vz-border-color: #32383e !important;
            --vz-link-color: ${lightenHex(primary, 0.30)} !important;
            --vz-link-hover-color: ${lightenHex(primary, 0.40)} !important;
            --vz-vertical-menu-item-hover-color: #fff !important;
            --vz-vertical-menu-item-active-color: #fff !important;
            --vz-vertical-menu-item-active-bg: rgba(255,255,255,.15) !important;
            --vz-vertical-menu-item-bg: rgba(255,255,255,.15) !important;
            --vz-vertical-menu-item-color: #89929b !important;
            --vz-vertical-menu-sub-item-color: #89929b !important;
            --vz-vertical-menu-sub-item-hover-color: #fff !important;
            --vz-vertical-menu-sub-item-active-color: #fff !important;
            ${shadcnPrimaryDark ? `--primary: ${shadcnPrimaryDark};\n            --ring: ${shadcnPrimaryDark};` : ""}
          }
        `;
        
        // Set page title
        document.title = tenantConfig.company;
        
        // Set favicon dynamically
        const faviconLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        if (faviconLink && tenantConfig.favicon) {
          faviconLink.href = tenantConfig.favicon;
        } else if (tenantConfig.favicon) {
          const newFavicon = document.createElement("link");
          newFavicon.rel = "icon";
          newFavicon.href = tenantConfig.favicon;
          document.head.appendChild(newFavicon);
        }
        
        setTenant(tenantConfig);
      } catch (error) {
        console.error("Failed to load tenant config:", error);
        // Aun en fallo, fijar el baseURL al default para no dejar odooInstance con la baseURL
        // relativa de dev (/api) — evita que staffs/me pegue al host equivocado.
        if (defaultTenant.apiUrl) {
          setAxiosBaseURL(defaultTenant.apiUrl);
        }
        setTenant(defaultTenant);
      } finally {
        setLoading(false);
      }
    };

    loadTenantConfig();
  }, []);

  // No renderizar los children hasta resolver el tenant: setAxiosBaseURL corre dentro de
  // loadTenantConfig ANTES de setLoading(false), así que gatear en `loading` garantiza que
  // odooInstance tenga la baseURL correcta del tenant antes de que monte cualquier consumidor
  // (p.ej. useUser -> crm/staffs/me). Sin esto había una CARRERA: en dev VITE_BACKEND_URL=/api
  // (relativa) y el primer me tras un hard-reload salía al host del front (o a api.celink.mx en
  // prod para tenants cuyo apiUrl real difiere: crmprerelease.celink.mx, crm.infynit.mx),
  // devolvía 401 y disparaba el logout global (validateUnauthorized).
  if (loading) {
    return null;
  }

  return (
    <TenantContext.Provider value={{ tenant, loading }}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
